import './App.sass';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useState, useEffect, useRef } from "react";
import tinycolor from "tinycolor2";
import { getEntityType, PoI, PoIType, Entity, Way, PoITypeI18N } from '@the-real-insight/tri-model';
import { ActionButton, Icon, IconButton, Toggle } from '@fluentui/react';
import Map, { Marker, Popup, Source, Layer, NavigationControl, ScaleControl, FullscreenControl, GeolocateControl, MapRef } from 'react-map-gl';
import { EntityFields } from './components/EntityFields';
import markerChargingStation from './marker-charging-station-icon.svg';
import markerUniversity from './marker-university-icon.svg';
import markerSchool from './marker-school-icon.svg';
import markerFireStation from './marker-fire-station-icon.svg';
import markerBillboard from './marker-billboard-icon.svg';
import markerChildCareFacility from './marker-child-care-facility-icon.svg';
import markerHospital from './marker-hospital-icon.svg';
import markerPharmacy from './marker-pharmacy-icon.svg';
import markerRestaurant from './marker-restaurant-icon.svg';
import markerHotel from './marker-hotel-icon.svg';
import markerAirport from './marker-airport-icon.svg';
import markerPoliceStation from './marker-police-station-icon.svg';
import markerFreewayMaintenanceOffice from './marker-freeway-maintenance-office-icon.svg';
import markerParkingLot from './marker-parking-lot-icon.svg';
import markerBusStop from './marker-bus-stop-icon.svg';
import markerChurch from './marker-church-icon.svg';
import markerHairdresser from './marker-hairdresser-icon.svg';
import markerDoctor from './marker-doctor-icon.svg';
import markerParcelStation from './marker-parcel-station-icon.svg';
import markerCareFacility from './marker-care-facility-icon.svg';
import markerCompany from './marker-company-icon.svg';
import markerTrainStation from './marker-train-station-icon.svg';
import { ReactComponent as PollutionSVG } from './marker-pollution-icon.svg';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InfoList from './object-properties/InfoList';
import { formatNumber0, formatNumber1 } from './utilities/format';
interface Properties {
    setObject?: (object: Entity) => void;
    showMiniMap?: boolean;
    query: string | null;
    data?: any;
    options?: any;
    pois?: any[]
    path?: { lat: number; lon: number }[];
}

const mapPoItoSVG = (poiType: any) => {
    switch (poiType) {
        case PoIType.chargingStation:
        case 'chargingPoint':
            return markerChargingStation;
        case PoIType.university: return markerUniversity;
        case PoIType.school: return markerSchool;
        case PoIType.fireStation: return markerFireStation;
        case PoIType.advertisementColumn:
        case PoIType.billboard: return markerBillboard;
        case PoIType.childCareFacility: return markerChildCareFacility;
        case PoIType.hospital: return markerHospital;
        case PoIType.pharmacy: return markerPharmacy;
        case PoIType.hotel: return markerHotel;
        case PoIType.restaurant: return markerRestaurant;
        case PoIType.airport: return markerAirport;
        case PoIType.policeStation: return markerPoliceStation;
        case PoIType.freewayMaintenanceOffice: return markerFreewayMaintenanceOffice;
        case PoIType.parkingLot: return markerParkingLot;
        case PoIType.busStop: return markerBusStop;
        case PoIType.church: return markerChurch;
        case PoIType.hairdresser: return markerHairdresser;
        case PoIType.doctor: return markerDoctor;
        case PoIType.parcelStation: return markerParcelStation
        case PoIType.careFacility: return markerCareFacility
        case PoIType.trainStation: return markerTrainStation;
        case 'company': return markerCompany;
        default: return markerSchool;
    }
}

const percentToColor = (value: number) => {
    const minimumColor = tinycolor('#00FF00');
    const maximumColor = tinycolor('#FF0000');
    const step = (maximumColor.toHsl().h - minimumColor.toHsl().h) / 100;

    return minimumColor.spin(step * value);
};

export default function MapAndImage(properties: Properties) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [bridges, setBridges] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [areas, setAreas] = useState([]);
    const [shapes, setShapes] = useState([]);
    const [plotsOfLand, setPlotsOfLand] = useState([]);
    const [options] = useState(properties.options || {});
    const [heatmapMax, setHeatmapMax] = useState(100);
    const [heatmapMin, setHeatmapMin] = useState(0);
    const [pieChartData, setPieChartData] = useState() as any;
    const [selectedEntity, setSelectedEntity] = useState() as any;
    const [hoveredEntity, setHoveredEntity] = useState() as any;
    const [showSatelliteImage, setShowSatelliteImage] = useState(false);
    const [pois, setPoIs] = useState([]) as any;
    const [ways, setWays] = useState([]) as any;
    const mapRef = useRef<MapRef>(null);

    // const calculateHeatmapDetails = (features: any) => {
    //     if (!options.heatmapField || !features || features.length === 0) {
    //         return;
    //     }

    //     let minimum = Number.MAX_VALUE;
    //     let maximum = Number.MIN_VALUE;

    //     features.forEach((feature: any) => {
    //         const value = feature[options.heatmapField];

    //         if (value !== undefined) {
    //             minimum = Math.min(minimum, value);
    //             maximum = Math.max(maximum, value);
    //         }
    //     });

    //     setHeatmapMin(minimum);
    //     setHeatmapMax(maximum);
    // }

    // const adjustBounds = (features: any) => {
    //     let minimumLat = Number.MAX_VALUE;
    //     let maximumLat = Number.MIN_VALUE;
    //     let minimumLon = Number.MAX_VALUE;
    //     let maximumLon = Number.MIN_VALUE;

    //     features.forEach((feature: any) => {
    //         if (feature.boundingBox) {
    //             minimumLat = Math.min(minimumLat, feature.boundingBox[0][0]);
    //             maximumLat = Math.max(maximumLat, feature.boundingBox[1][0]);
    //             minimumLon = Math.min(minimumLon, feature.boundingBox[0][1]);
    //             maximumLon = Math.max(maximumLon, feature.boundingBox[1][1]);
    //         } else if (feature.shape) {
    //             feature.shape.forEach((entry: any) => {
    //                 if (entry.forEach) {
    //                     entry.forEach((point: any) => {
    //                         minimumLat = Math.min(minimumLat, point[0]);
    //                         maximumLat = Math.max(maximumLat, point[0]);
    //                         minimumLon = Math.min(minimumLon, point[1]);
    //                         maximumLon = Math.max(maximumLon, point[1]);
    //                     });
    //                 } else if (entry.lat) {
    //                     minimumLat = Math.min(minimumLat, entry.lat);
    //                     maximumLat = Math.max(maximumLat, entry.lat);
    //                     minimumLon = Math.min(minimumLon, entry.lon);
    //                     maximumLon = Math.max(maximumLon, entry.lon);
    //                 }
    //             });
    //         }
    //     });

    //     setBounds([[minimumLat, minimumLon], [maximumLat, maximumLon]] as any);
    // }

    // useEffect(() => {
    //     const f = async () => {

    //         if (properties.objectClass === 'bridge') {
    //             const newBridges = await userAPI.getBridges(properties.query);

    //             setBridges(newBridges);

    //             newBridges.forEach((bridge: any) => {
    //                 bridge.class = 'Bridge';
    //             });
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, [map, properties.objectClass, properties.query]);

    // useEffect(() => {
    //     const f = async () => {
    //         if (properties.objectClass === 'building') {
    //             let newBuildings;

    //             newBuildings = properties.data.buildings;

    //             setBuildings(newBuildings);

    //             newBuildings.forEach((building: any) => {
    //                 building.class = 'Building';
    //                 // @TODO Temporary
    //                 building.id = building.id.replace('undefined', 'DE')
    //             });

    //             console.log('Buildings >>>', newBuildings);

    //             calculateHeatmapDetails(newBuildings);
    //             adjustBounds(newBuildings);
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, [map]);

    // useEffect(() => {
    //     const f = async () => {
    //         if (properties.objectClass === 'Tile') {
    //             let newAreas;

    //             newAreas = properties.data.areas;

    //             // TODO Hack

    //             newAreas.forEach((area: any) => {
    //                 area.class = 'Tile';
    //             });

    //             setAreas(newAreas);
    //             calculateHeatmapDetails(newAreas);
    //             adjustBounds(newAreas);
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, []);

    // useEffect(() => {
    //     const f = async () => {
    //         if (properties.objectClass === 'LocalGovernment' && properties.data.localGovernments) {
    //             let newLocalGovernments;
    //             newLocalGovernments = properties.data.localGovernments;

    //             // TODO Hack

    //             newLocalGovernments.forEach((area: any) => {
    //                 area.class = 'LocalGovernment';
    //             });

    //             setShapes(newLocalGovernments);
    //             adjustBounds(newLocalGovernments);
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, []);

    // useEffect(() => {
    //     const f = async () => {
    //         if (properties.objectClass === 'PostalCode' && properties.data.postalCodes) {
    //             let newPostalCodes;
    //             newPostalCodes = properties.data.postalCodes;

    //             // TODO Hack

    //             newPostalCodes.forEach((postalCode: any) => {
    //                 postalCode.class = 'PostalCode';
    //             });

    //             console.log('Postal codes >>>', newPostalCodes);

    //             setShapes(newPostalCodes);
    //             adjustBounds(newPostalCodes);
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, []);

    // useEffect(() => {
    //     const f = async () => {
    //         if (properties.objectClass === 'PlotOfLand' && properties.data.plotsOfLand) {
    //             let newPlotsOfLand;
    //             newPlotsOfLand = properties.data.plotsOfLand;

    //             // TODO Hacks

    //             newPlotsOfLand.forEach((plotOfLand: any) => {
    //                 plotOfLand.class = 'PlotOfLand';
    //             });

    //             console.log('Plots of land >>>', newPlotsOfLand);

    //             setPlotsOfLand(newPlotsOfLand);
    //             adjustBounds(newPlotsOfLand);
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, []);

    // useEffect(() => {
    //     const f = async () => {
    //         if (properties.data.points && properties.options.pieChart) {

    //             console.log('Pie Chart Data ====>>>', properties.data.points);

    //             setPieChartData(properties.data.points)
    //             //adjustBounds(newPlotsOfLand);
    //         }
    //     };

    //     f().catch((error) => console.error(error))
    // }, []);

    // useEffect(() => {
    //     try {
    //         if (map && bounds) {
    //             map.fitBounds(bounds);
    //         }
    //     } catch (error) {
    //         console.error(error);
    //     }
    // }, [map, bounds]);

    useEffect(() => {
        if (properties.options.entityType === 'PoI') {
            const newPois = properties.data.map((raw: any) => {
                if (raw._type) {
                    const entityType = getEntityType(raw._type);

                    // TODO Hack to keep poiType for LegalPerson
                    
                    const poiType = raw.poiType;

                    raw = new entityType(raw);

                    raw.poiType = poiType;

                    return raw;
                } else {
                    return raw;
                }
            });

            setPoIs(newPois);
        } else if (properties.options.entityType === 'Way') {
            const newWays = properties.data.map((raw: any) => {
                if (raw._type) {
                    const entityType = getEntityType(raw._type);

                    return new entityType(raw);
                } else {
                    return raw;
                }
            });

            setWays(newWays);
        }
    }, [properties.data]);

    const openPopup = (poi: any) => {
        setSelectedEntity(poi)
    }

    useEffect(() => {
        if (mapRef.current) {
            const map = mapRef.current.getMap();

            const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
                const features = map.queryRenderedFeatures(e.point, { layers: ways.map((_: Way, i: number) => `path-${i}`) });

                // Reset all paths to default style (purple)
                ways.forEach((_: Way, i: number) => {
                    map.setPaintProperty(`path-${i}`, 'line-width', 10);
                    map.setPaintProperty(`path-${i}`, 'line-color', '#EE0000');
                });

                // Highlight the path under the cursor (red)
                if (features.length > 0) {
                    const layerId = features[0].layer.id;
                    const index = parseInt(layerId.split('-')[1]);
                    const way = ways[index];
                    map.setPaintProperty(layerId, 'line-width', 10);
                    map.setPaintProperty(layerId, 'line-color', '#FF0000');
                }
            };

            const handleClick = (e: mapboxgl.MapMouseEvent) => {
                const features = map.queryRenderedFeatures(e.point, { layers: ways.map((_: Way, i: number) => `path-${i}`) });

                if (features.length > 0) {
                    const layerId = features[0].layer.id;
                    const index = parseInt(layerId.split('-')[1]);
                    const way = ways[index];

                    setHoveredEntity(way);
                }
            };

            map.on('mousemove', handleMouseMove);
            map.on('click', handleClick);

            return () => {
                map.off('mousemove', handleMouseMove);
                map.off('click', handleClick);
            };
        }
    }, [ways]);

    return <div className="displayFlex flexDirectionColumn gapM">
        <Toggle
            label="Show satellite image"
            checked={showSatelliteImage}
            onChange={(_: any, showSatelliteImage?: boolean) => setShowSatelliteImage(!!showSatelliteImage)}
            role="checkbox"
        />
        <Map mapboxAccessToken="pk.eyJ1IjoibWFyY2dpbGxlc2VwZWhyaSIsImEiOiJjbTFoOWszeHQwYzR6MmpzYXh0OWo0cWF1In0.oCmn9LnN9lZavqGgDL_Y2w"
            ref={mapRef}
            initialViewState={{
                longitude: properties.options.bounds ? undefined : properties.options.centerLon || 8.670636045048122,
                latitude: properties.options.bounds ? undefined : properties.options.centerLat || 50.10495071520639,
                bounds: properties.options.bounds || undefined,
                zoom: 15
            }}
            style={{ width: '100%', height: 800 }}
            mapStyle={showSatelliteImage ? 'mapbox://styles/mapbox/satellite-streets-v9' : 'mapbox://styles/mapbox/streets-v9'}
        >
            <NavigationControl position="top-right" />
            <ScaleControl position="bottom-right" />
            <FullscreenControl position="top-right" />
            <GeolocateControl position="top-right" />
            {(ways || []).map((way: Way, index: number) => (
                <Source
                    key={`path-${index}`}
                    type="geojson"
                    data={{
                        type: 'Feature',
                        properties: {},
                        geometry: {
                            type: 'LineString',
                            coordinates: way.shape.map((point: any) => [point.lon, point.lat])
                        }
                    }}
                >
                    <Layer
                        id={`path-${index}`}
                        type="line"
                        paint={{
                            'line-color': '#BB0000',
                            'line-width': 10,
                            'line-opacity': 0.8
                        }}
                    />
                </Source>
            ))}
            {pois.map((poi: PoI) => <Marker
                longitude={poi.lon}
                latitude={poi.lat}>
                <div className={`marker`} onClick={() => {
                    setHoveredEntity(poi);
                }}>
                    <span className={`pin ${poi.poiType}`}>
                        {poi.poiType as string === 'pollutionData'
                            ?
                            <PollutionSVG className="pollutionData" style={{ fill: (poi as any).pfasSum > 100 ? '#FF0000' : ((poi as any).pfasSum > 0 ? '#FFA500' : '#AAAAAA') }} />
                            :
                            <img src={mapPoItoSVG(poi.poiType)} />}
                    </span>
                </div>
            </Marker>)}
            {hoveredEntity ?
                <Popup
                    latitude={hoveredEntity.lat}
                    longitude={hoveredEntity.lon}
                    onClose={() => setHoveredEntity(null)}
                    closeButton={true}
                    closeOnClick={false}
                    offset={0}
                    style={{ minWidth: '500px' }}
                >
                    <div className="paddingS">
                        {hoveredEntity.poiType === 'pollutionData' ? <div>
                            <div>
                                <div className='textHeader marginBottomS'>PFAS Schadstoffdaten</div>
                                <div className='displayFlex flexDirectionRow gapS textM'>
                                    <div className='width100'>Datensatz</div>
                                    <div>{hoveredEntity.datasetId} - {hoveredEntity.datasetName}</div>
                                </div>
                                <div className='displayFlex flexDirectionRow gapS textM'>
                                    <div className='width100'>Quelle</div>
                                    <div><a href={hoveredEntity.source} target='_blank'>{hoveredEntity.source}</a></div>
                                </div>
                                <div className='displayFlex flexDirectionRow gapS textM'>
                                    <div className='width100'>Matrix</div>
                                    <div>{hoveredEntity.matrix}</div>
                                </div>
                                <div className='displayFlex flexDirectionRow gapS textM'>
                                    <div className='width100'>Type</div>
                                    <div>{hoveredEntity.type}</div>
                                </div>
                                <div className='displayFlex flexDirectionRow gapS textM'>
                                    <div className='width100'>Sektor</div>
                                    <div>{hoveredEntity.sector}</div>
                                </div>
                                <div className='displayFlex flexDirectionRow gapS textM'>
                                    <div className='width100'>Kategorie</div>
                                    <div>{hoveredEntity.category}</div>
                                </div>
                                <div className='displayFlex flexDirectionRow gapS textM'>
                                    <div className='width100'>PFAS-Summe</div>
                                    {hoveredEntity.pfasSum !== undefined ? <div>{hoveredEntity.pfasSum} {hoveredEntity.unit}</div> : <div>-</div>}
                                </div>
                                {(hoveredEntity.pfasDetails || []).map((details: any) => <div className='displayFlex flexDirectionRow gapS textM'>
                                    <div className='width100'>{details.substance}</div>
                                    <div className='width50'>{details.value}</div>
                                    <div className='width50'>{details.unit}</div>
                                </div>)}
                            </div>
                        </div> :
                            <div>
                                {(() => {
                                    switch (hoveredEntity.getTypeName()) {
                                        case 'Bridge':
                                            return <div>
                                                <div className='textHeader'>{hoveredEntity.name || t('bridgePropertiesPanel.unnamedBridge')}</div>
                                                <ActionButton className='marginTopS' iconProps={{ iconName: 'OpenInNewWindow' }} onClick={() => {
                                                    navigate(`/entity?selectedEntityType=${hoveredEntity.getTypeName()}&selectedEntity=${hoveredEntity._id}`);
                                                }}>Details anzeigen</ActionButton>
                                                <div className='marginTopS'>
                                                    <InfoList list={[{
                                                        label: t('bridgePropertiesPanel.usageType'),
                                                        value: hoveredEntity.usageType,
                                                    }, {
                                                        label: t('bridgePropertiesPanel.length'),
                                                        value: formatNumber0(hoveredEntity.length),
                                                        suffix: 'm',
                                                        align: 'right',
                                                    }, {
                                                        label: t('bridgePropertiesPanel.width'),
                                                        value: formatNumber1(hoveredEntity.width),
                                                        suffix: 'm',
                                                        align: 'right',
                                                    }, {
                                                        label: t('bridgePropertiesPanel.height'),
                                                        value: formatNumber1(hoveredEntity.height),
                                                        suffix: 'm',
                                                        align: 'right',
                                                    }]} />
                                                </div>
                                            </div>;
                                        case 'LegalPerson':
                                            return <div>
                                                <div className='textHeader'>{hoveredEntity.name || t('bridgePropertiesPanel.unnamedBridge')}</div>
                                                <ActionButton className='marginTopS' iconProps={{ iconName: 'OpenInNewWindow' }} onClick={() => {
                                                    navigate(`/entity?selectedEntityType=${hoveredEntity.getTypeName()}&selectedEntity=${hoveredEntity._id}`);
                                                }}>Details anzeigen</ActionButton>
                                                <div className='marginTopS'>
                                                    <InfoList list={[{
                                                        label: t('legalPersonPropertiesPanel.name'),
                                                        value: hoveredEntity.name,
                                                    }, {
                                                        label: t('legalPersonPropertiesPanel.purpose'),
                                                        value: hoveredEntity.purpose,
                                                    }, {
                                                        label: t('legalPersonPropertiesPanel.address'),
                                                        value: hoveredEntity.address,
                                                    }, {
                                                        label: t('legalPersonPropertiesPanel.tradeRegisterId'),
                                                        value: hoveredEntity.tradeRegisterId,
                                                    }, {
                                                        label: t('legalPersonPropertiesPanel.tradeRegisterCity'),
                                                        value: hoveredEntity.tradeRegisterCity,
                                                    }]} />
                                                </div>
                                            </div>;
                                        default:
                                            return <div>
                                                <div className="textHeader">{(PoITypeI18N['de'] as any)[hoveredEntity.poiType]}</div>
                                                {/* <ActionButton iconProps={{ iconName: 'OpenInNewWindow' }} onClick={() => {
                                                    navigate(`/entity?selectedEntityType=${hoveredEntity.getTypeName()}&selectedEntity=${hoveredEntity._id}`);
                                                }}>Details anzeigen</ActionButton> */}
                                                <EntityFields entity={hoveredEntity} hiddenFields={['poiType', 'lat', 'lon', 'osmId', 'osmType', 'country']} valueWidth="400px"></EntityFields>
                                            </div>
                                    }
                                })()}
                            </div>}
                    </div>
                </Popup>
                :
                <></>
            }
        </Map>
    </div>;
}